import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { ApiError, api, getToken, setToken } from './api.js'

const AuthContext = createContext(null)

export function AuthProvider ({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(Boolean(getToken()))
  const [offline, setOffline] = useState(false)

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null)
      setLoading(false)
      return null
    }
    try {
      const { user } = await api.get('/auth/me')
      setUser(user)
      setOffline(false)
      return user
    } catch (err) {
      // Only a real auth failure ends the session. A server that is restarting
      // or briefly unreachable must not cost anyone their sign-in mid-demo.
      if (err instanceof ApiError && err.status === 0) {
        setOffline(true)
        return null
      }
      setToken(null)
      setUser(null)
      setOffline(false)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { refresh() }, [refresh])

  // An expired token anywhere in the app drops the session cleanly.
  useEffect(() => {
    const onOut = () => setUser(null)
    window.addEventListener('aqua:signed-out', onOut)
    return () => window.removeEventListener('aqua:signed-out', onOut)
  }, [])

  const signIn = useCallback(async (username, password) => {
    const { token } = await api.post('/auth/login', { username, password })
    setToken(token)
    return refresh()
  }, [refresh])

  const signOut = useCallback(() => {
    setToken(null)
    setUser(null)
    setOffline(false)
  }, [])

  const value = useMemo(
    () => ({ user, loading, offline, hasToken: Boolean(getToken()), signIn, signOut, refresh }),
    [user, loading, offline, signIn, signOut, refresh]
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => useContext(AuthContext)

export const homeFor = user => ({
  admin: '/admin',
  operator: '/operator',
  hatchery: '/hatchery',
  liaison: '/liaison'
}[user?.role] || '/login')

/** Admin doubles as the operator desk during a demo. */
export function canAccess (user, roles) {
  if (!user) return false
  if (roles.includes(user.role)) return true
  return user.role === 'admin' && (roles.includes('operator') || roles.includes('liaison'))
}
